import { observable, action, decorate, toJS } from 'mobx';
import { persist } from 'mobx-persist';

/** Class representing global state of the app.
 * Store is decorated with mobx.
 * It uses the following mobx and mobx-persist decorators:
 * - observable: variable is set as observable (observers will be notified if the variable is updated)
 * - persist: variable will be persisted in a storage
 * - action(.bound): the function is enabled to update the store. Its "this" is bound to the store.
 * - computed: after an observable update, this function automatically updates a complex variable
 * @see {@link https://mobx.js.org/|mobx}
 * @see {@link https://www.npmjs.com/package/mobx-persist|mobx-persist}
 */
class Store {
  /**
   * initializes store.
   * @constructor
   */
  constructor() {
    this.getComponents = () => {
      return this.previewConfiguration.configuration.homePage.boxes;
    };
    this.getComponentFromIndex = index => {
      return this.previewConfiguration.configuration.homePage.boxes[index];
    };
    // project information
    this.projectName = null;
    this.displayName = '';
    this.projectId = null;
    this.hostKey = '';
    this.baseUrl = '';

    // snackbar
    this.snackbarOpen = false;
    this.snackbarMessage = undefined;
    this.snackbarError = false;

    // user data
    this.loggedUser = null;

    // configuration
    this.renderPreviewConfiguration = null;
    this.previewConfiguration = null;
    this.previewConfigurations = [];
    this.toolSetPreviewConfiguration = null;
    this.forceUpdate = null;
    this.uploading = false;
    this.loaderCount = 0;
    this.virtualPhotoCurrentCategory = '';
    this.customStyles = {};
    this.configurationMode = false;
  }

  /* ACTIONS */
  /**
   * @param {object} obj an object containing all project information
   */
  setProjectInfo(obj) {
    if (obj) {
      this.projectName = obj.name;
      this.displayName = obj.displayName;
      this.projectId = obj.id;
      this.hostKey = obj.hostKey;
      this.enabledTools = obj.enabledTools;
    }
  }
  getProjectId() {
    return this.projectId;
  }
  getProjectAssetFolder() {
    return this.displayName;
  }

  /**
   * sets the logged user
   * @param {Object} userData the logged user object
   */
  setLoggedUser(userData) {
    if (!this.loggedUser) {
      this.loggedUser = userData;
    }
  }

  /**
   * sets snackbar open/closed
   * @param {boolean} val
   * @param {string} mes message to display
   */
  setSnackbar(val, mes) {
    if (val !== this.snackbarOpen) {
      this.snackbarMessage = mes;
      this.snackbarOpen = val;
    }
  }

  /**
   * sets snackbar error open/closed
   * @param {boolean} err
   * @param {string} mes message to display
   */
  setSnackbarError(err, mes) {
    if (err !== this.snackbarError) {
      this.snackbarMessage = mes;
      this.snackbarError = err;
    }
  }
  setToolSetPreviewConfiguration(setPreviewConfiguration) {
    this.toolSetPreviewConfiguration = setPreviewConfiguration;
  }
  setForceUpdate(forceUpdate) {
    this.forceUpdate = forceUpdate;
  }
  setRenderPreviewConfiguration(configuration) {
    this.renderPreviewConfiguration = Object.assign({}, toJS(configuration));
    this.previewConfiguration = Object.assign({}, toJS(configuration));
  }
  setPreviewConfiguration(configuration) {
    this.previewConfiguration = Object.assign({}, toJS(configuration));
  }
  setVirtualPhotoCurrentCategory(virtualPhotoCurrentCategory) {
    this.virtualPhotoCurrentCategory = virtualPhotoCurrentCategory;
  }
  getPreviewConfigurationId() {
    return this.previewConfiguration.id;
  }
  setPreviewConfigurations(configurations) {
    this.previewConfigurations = configurations;
  }
  getPreviewConfiguration() {
    return this.previewConfiguration;
  }
  getRenderPreviewConfiguration() {
    return this.renderPreviewConfiguration;
  }
  getComponentFromConfiguration(path) {
    const pathArray = path.split('#');
    let componentsArray = [];
    let i = 0;
    let obj = this.previewConfiguration.configuration;
    componentsArray.push(obj);
    let currentPage = 'HomePage';
    while (obj && i < pathArray.length) {
      let currentPath = pathArray[i];
      switch (currentPage) {
        case 'HomePage':
          if (currentPath === '') {
            obj = obj.homePage;
          } else {
            const component = obj.homePage.boxes.filter(c => {
              return c.navigation === currentPath;
            })[0];
            if (!component) {
              obj = undefined;
            } else {
              currentPage = component.component;
              obj = component;
            }
          }
          break;
        case 'ComponentSwitcher':
        case 'InteriorExperienceSplit':
          const component = obj.componentParams.components.filter(c => c.navigation === currentPath)[0];
          if (!component) {
            obj = undefined;
          } else {
            currentPage = component.component;
            obj = component;
          }
          break;
        default:
          obj = undefined;
          break;
      }
      componentsArray.push(obj);
      i++;
    }
    return componentsArray;
  }
  getCurrentMenu(page) {
    const pages = page.split('#');
    return pages[pages.length - 1];
  }
  getFieldFromPreviewConfiguration(field) {
    return this.getFieldFromConfig(this.previewConfiguration.configuration, field);
  }
  setFieldToPreviewConfiguration(field, value) {
    return this.setFieldToConfig(this.previewConfiguration.configuration, field, value);
  }
  getFieldFromConfig(config, field) {
    const path = field.split('.');
    let obj = config;
    let i = 0;
    while (obj && i < path.length) {
      obj = obj[path[i]];
      i++;
    }
    return obj;
  }
  setFieldToConfig(config, field, value) {
    const path = field.split('.');
    let obj = config;
    let i = 0;
    while (obj && i < path.length - 1) {
      obj = obj[path[i]];
      i++;
    }
    if (obj) {
      obj[path[path.length - 1]] = value;
      return true;
    } else {
      return false;
    }
  }
  addElementToListFromPreviewConfiguration(field, value) {
    return this.addElementToList(this.previewConfiguration.configuration, field, value);
  }
  addElementToList(config, field, value) {
    const path = field.split('.');
    let obj = config;
    let i = 0;
    while (obj && i < path.length - 1) {
      obj = obj[path[i]];
      i++;
    }
    if (obj && obj[path[path.length - 1]]) {
      obj[path[path.length - 1]].push(value);
      return true;
    } else {
      return false;
    }
  }
  removeElementFromListFromPreviewConfiguration(field, index) {
    return this.addElementToList(this.previewConfiguration.configuration, field, index);
  }
  removeElementFromList(config, field, index) {
    const path = field.split('.');
    let obj = config;
    let i = 0;
    while (obj && i < path.length - 1) {
      obj = obj[path[i]];
      i++;
    }
    if (obj && obj[path[path.length - 1]] && obj[path[path.length - 1]].length > index) {
      obj[path[path.length - 1]].splice(index, 1);
      return true;
    } else {
      return false;
    }
  }
  setUploading(val) {
    this.uploading = val;
  }
  updateLoaderCount() {
    this.loaderCount += 1;
  }
  synchronizeWholeConfiguration() {
    const configuration = Object.assign({}, toJS(this.getRenderPreviewConfiguration()));
    this.setPreviewConfiguration(configuration);
    if (this.toolSetPreviewConfiguration) {
      this.toolSetPreviewConfiguration(configuration);
    }
    if (this.forceUpdate) {
      this.forceUpdate();
    }
  }
  setCustomStyles(customStyles) {
    this.customStyles = customStyles;
  }
  setConfigurationMode(configurationMode) {
    this.configurationMode = configurationMode;
  }
  setBaseUrl(baseUrl) {
    this.baseUrl = baseUrl;
  }
}
decorate(Store, {
  projectName: [persist, observable],
  displayName: [persist, observable],
  projectId: [persist, observable],
  hostKey: [persist, observable],
  loggedUser: [persist('object'), observable],
  renderPreviewConfiguration: [persist('object'), observable],
  previewConfiguration: [persist('object'), observable],
  previewConfigurations: [persist('object'), observable],
  snackbarOpen: observable,
  snackbarMessage: observable,
  snackbarError: observable,
  uploading: observable,
  loaderCount: observable,
  configurationMode: observable,
  setProjectInfo: action.bound,
  getProjectId: action.bound,
  getProjectAssetFolder: action.bound,
  setLoggedUser: action.bound,
  setToolSetPreviewConfiguration: action.bound,
  setForceUpdate: action.bound,
  setPreviewConfiguration: action.bound,
  setRenderPreviewConfiguration: action.bound,
  getPreviewConfiguration: action.bound,
  getRenderPreviewConfiguration: action.bound,
  setPreviewConfigurations: action.bound,
  getFieldFromPreviewConfiguration: action.bound,
  setFieldToPreviewConfiguration: action.bound,
  getFieldFromConfig: action.bound,
  setFieldToConfig: action.bound,
  getComponents: action.bound,
  getComponentFromIndex: action.bound,
  getComponentFromConfiguration: action.bound,
  getCurrentMenu: action.bound,
  addElementToListFromPreviewConfiguration: action.bound,
  addElementToList: action.bound,
  removeElementFromListFromPreviewConfiguration: action.bound,
  removeElementFromList: action.bound,
  updateLoaderCount: action.bound,
  synchronizeWholeConfiguration: action.bound,
  setUploading: action.bound,
  setCustomStyles: action.bound,
  setConfigurationMode: action.bound,
  setBaseUrl: action.bound
});
export default Store;